/**
 * Checkout Error Handler Utilities
 * Centralizes error handling logic for checkout flow to reduce code duplication
 */

import type { UseToastOptions } from '@chakra-ui/react';
import { buyerCaptureException } from '@diamond/shared/utils';
import { AxiosError } from 'axios';

/**
 * Toast function type (from showToast utility)
 */
type ToastFunction = (
  toast: any,
  status: 'success' | 'error' | 'warning' | 'info',
  message: string,
  options?: UseToastOptions
) => void;

/**
 * Checkout error context for Sentry tagging
 */
export type CheckoutErrorContext =
  | 'upload-document'
  | 'patch-order'
  | 'submit-order'
  | 'validate-order';

/**
 * Error handling result
 */
export interface ErrorHandlingResult {
  message: string;
  shouldThrow: boolean;
}

export const CHECKOUT_PAYMENT_UNAVAILABLE_MESSAGE =
  'Metode pembayaran tidak tersedia untuk pesanan ini';

/**
 * True when backend rejects a regular payment method for estimated-weight
 * availability (TRD #7263 / FE Task 5).
 */
export const isCheckoutPaymentAvailabilityError = (
  error: unknown
): error is AxiosError => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  if (error.response?.status !== 422) {
    return false;
  }

  const message = error.response?.data?.message;
  if (typeof message !== 'string') {
    return false;
  }

  return (
    message === CHECKOUT_PAYMENT_UNAVAILABLE_MESSAGE ||
    message.toLowerCase().includes('metode pembayaran tidak tersedia')
  );
};

/**
 * Captures checkout error to Sentry with consistent tagging
 *
 * @param error - The error object (AxiosError or Error)
 * @param context - Context tag for identifying error source
 * @param metaId - Optional meta ID for additional context
 *
 * @example
 * captureCheckoutError(error, 'patch-order', metaId);
 */
export const captureCheckoutError = (
  error: unknown,
  context: CheckoutErrorContext,
  metaId?: string
): void => {
  if (error instanceof AxiosError) {
    buyerCaptureException(error.response?.data, (scope) => {
      scope.setTag('checkout-error', context);
      if (metaId) {
        scope.setTag('meta-id', metaId);
      }
      scope.setLevel('error');
    });
  }
};

/**
 * Handles checkout errors with toast notification and Sentry tracking
 *
 * @param error - The error object
 * @param context - Context tag for identifying error source
 * @param showToast - Toast notification function
 * @param toast - Chakra UI toast instance
 * @param fallbackMessage - Fallback error message if error.message is unavailable
 * @param metaId - Optional meta ID for Sentry context
 * @returns Error handling result with message and throw instruction
 *
 * @example
 * // In catch block:
 * handleCheckoutError(
 *   error,
 *   'upload-document',
 *   showToast,
 *   toast,
 *   'Gagal upload dokumen',
 *   metaId
 * );
 */
export const handleCheckoutError = (
  error: unknown,
  context: CheckoutErrorContext,
  showToast: ToastFunction,
  toast: any,
  fallbackMessage: string,
  metaId?: string
): ErrorHandlingResult => {
  // Capture to Sentry
  captureCheckoutError(error, context, metaId);

  // Extract error message
  let errorMessage = fallbackMessage;

  if (error instanceof AxiosError) {
    // Try to get message from Axios error response
    errorMessage =
      error.response?.data?.message || error.message || fallbackMessage;
  } else if (error instanceof Error) {
    // Regular Error object
    errorMessage = error.message || fallbackMessage;
  }

  // Show toast notification
  showToast(toast, 'error', errorMessage);

  return {
    message: errorMessage,
    shouldThrow: true,
  };
};

/**
 * Handles checkout success with toast notification
 *
 * @param showToast - Toast notification function
 * @param toast - Chakra UI toast instance
 * @param message - Success message
 *
 * @example
 * handleCheckoutSuccess(showToast, toast, 'Berhasil Checkout!');
 */
export const handleCheckoutSuccess = (
  showToast: ToastFunction,
  toast: any,
  message: string
): void => {
  showToast(toast, 'success', message);
};
